import { translate } from '@jsverse/transloco';

import { titleCase } from 'humanize-plus';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { NewspaperService } from 'app/providers/storage-api/newspaper.service';
import { Utils } from 'app/utils/utils';
import { Comparison, Widget } from '../../../../../providers/bridge-api/dashboard.service';
import { WidgetChartOptions } from '../../widget-chart/widget-chart.component';
import { Article } from './article';
import { Journalist, Story, formatStoryDate } from './journalist';

export default class NumericMetadataOverTimeJournalist implements Journalist {
  constructor(
    private widget: Widget<WidgetChartOptions>,
    private comparisonService: ComparisonHandlerService,
    private newspaperService: NewspaperService,
    private filterService: FilterService
  ) {}

  widgetSettingsHash(): number {
    const widgetSettings = {
      numericMetadataFields: this.widget.payload.numericMetadataFields,
      dateInterval: this.widget.payload.dateInterval,
    };
    return Utils.hashCode(JSON.stringify(widgetSettings));
  }

  writeStory(article: Article, errorCallback?: (...args) => void): Story {
    const transformedData = article.transform();
    if (!transformedData) {
      return;
    }

    let comparison: Comparison;
    if (this.widget.payload.numericMetadataFields?.length === 1) {
      comparison = this.comparisonService.getComparison(this.widget.payload.comparison);
      if (!comparison?.filters.length) {
        comparison = undefined;
      }
    }

    const contents = transformedData.contentData.map((value, idx) => {
      if (!value) {
        let fieldName = this.widget.payload.numericMetadataFields[idx]?.label;
        if (comparison) {
          fieldName = this.comparisonService.filterDisplayValueExtractorFn(comparison.type)(
            comparison.filters[idx]
          );
        }

        return {
          subHeader: translate('widgets.newspaper-widglet.articles.nmot.subheaders.missing', {
            fieldName,
          }),
        };
      }

      const shouldFetchInsight =
        comparison?.filters.length > 1 || idx === transformedData.contentData.length - 1;
      const metadataField = this.widget.payload.numericMetadataFields.find(
        (f) => f.fieldId === value.field
      );

      return this.writeContents(
        article,
        value,
        idx,
        metadataField,
        comparison,
        shouldFetchInsight,
        errorCallback
      );
    });

    return {
      heading: formatStoryDate(
        transformedData.headerData,
        article.timezone,
        this.widget.payload.dateInterval
      ),
      contents,
    };
  }

  private writeContents(
    article: Article,
    resultValue,
    resultIndex: number,
    metadataField,
    comparison: Comparison,
    shouldFetchInsight: boolean,
    errorCallback
  ) {
    let translationKey = 'widgets.newspaper-widglet.articles.nmot.subheaders.';
    if (comparison) {
      translationKey += 'comparison.';
    }
    translationKey += resultValue.scoreType;

    const translationParams: any = this.assembleSubHeaderTranslationParams(
      resultValue,
      metadataField
    );
    if (comparison) {
      const bucket = this.comparisonService.filterDisplayValueExtractorFn(comparison.type)(
        comparison.filters[resultIndex]
      );
      translationParams.bucket = bucket;
    }

    const contents = {
      subHeader: translate(translationKey, translationParams),
    };

    if (shouldFetchInsight) {
      this.fetchInsight(article, resultIndex, comparison, contents, errorCallback);
    }

    return contents;
  }

  private assembleSubHeaderTranslationParams(resultValue, metadataField) {
    switch (resultValue.scoreType) {
      case 'COUNT':
        return { score: resultValue.score };
      case 'BOX':
        const score =
          metadataField.scoring === 'top' ? resultValue.topScore : resultValue.bottomScore;

        return {
          boxDirection: titleCase(metadataField.scoring),
          boxSize: metadataField.boxSize,
          fieldName: metadataField.label,
          score: Utils.round(score, 1),
        };
      default:
        return {
          score: Utils.round(resultValue.score, 1),
          fieldName: metadataField?.label,
        };
    }
  }

  private fetchInsight(
    article: Article,
    resultIndex,
    comparison: Comparison,
    contents,
    errorCallback
  ) {
    if (!article.scene) {
      contents.insight = translate('widgets.newspaper-widglet.no-scene');
      return;
    }

    let queryOptions = article.documentQueryOptions[0];
    if (comparison?.filters.length > 1) {
      queryOptions = article.documentQueryOptions[resultIndex];
    }

    const query = this.filterService.getQueryForDocuments(queryOptions);

    contents.insight = '';

    this.newspaperService
      .postNewspaperSmartSummary(
        article.collectionName,
        article.analysisGroupId,
        article.analysisId,
        article.timezone,
        article.scene,
        query,
        article.projectId
      )
      .then((res) => (contents.insight = res.text))
      .catch((err) => {
        contents.insight = translate('widgets.newspaper-widglet.scene-error');
        errorCallback?.(err);
      });
  }
}
